import type { LocationQueryValue } from 'vue-router';
import type { Ref } from 'vue';
import { useScrollLock } from '@vueuse/core';

interface DrupalSearchController {
  searchQuery: Ref<string>;
  searchTerms: LocationQueryValue | LocationQueryValue[] | undefined;
  isSearchPanelOpen: Ref<boolean>;
  onSearch: () => Promise<void>;
  handleToggleSearchPanel: () => void;
}

interface DrupalSearchOptions {
  targetPath?: string;
  preserveSort?: boolean;
}

interface DrupalSearchTargetRule {
  pattern: RegExp;
  targetPath: string;
}

const DEFAULT_SEARCH_TARGET_PATH = '/search';

/**
 * Route-based search destinations.
 *
 * Add new section search experiences here as they are introduced. The first
 * matching rule wins; anything unmatched falls back to the global site search.
 */
const SEARCH_TARGET_RULES: DrupalSearchTargetRule[] = [
  {
    pattern: /^\/knowledge-center\/the-node(?:\/|$)/,
    targetPath: '/knowledge-center/the-node',
  },
];

function normalizeRoutePath(path: string): string {
  const normalizedPath = path.replace(/\/+$/, '');
  return normalizedPath || '/';
}

function resolveSearchTargetPath(
  currentPath: string,
  targetPathOverride?: string
): string {
  if (targetPathOverride) {
    return normalizeRoutePath(targetPathOverride);
  }

  const normalizedCurrentPath = normalizeRoutePath(currentPath);
  const matchedTarget = SEARCH_TARGET_RULES.find(({ pattern }) =>
    pattern.test(normalizedCurrentPath)
  );

  if (matchedTarget) {
    return matchedTarget.targetPath;
  }

  if (normalizedCurrentPath === DEFAULT_SEARCH_TARGET_PATH) {
    return currentPath;
  }

  return DEFAULT_SEARCH_TARGET_PATH;
}

/**
 * Normalizes a route query value into the single string used by the search UI.
 *
 * Vue Router can expose query params as a string, array, or undefined depending
 * on how the URL was built. The search form only supports a single keyword
 * value, so this helper keeps the composable's state shape predictable.
 */
function getSearchQueryValue(
  queryValue: string | string[] | null | undefined
): string {
  if (Array.isArray(queryValue)) {
    return queryValue[0] ?? '';
  }

  return queryValue ?? '';
}

/**
 * Shared Nuxt search controller for Drupal search experiences.
 *
 * Flow:
 * - hydrate the input model from the current `keys` query param
 * - keep the input model synced when navigation changes the route
 * - submit by rebuilding the query string and pushing a new route
 *
 * The composable uses `useState` so any component on the same route can read
 * and update the same search keyword without prop drilling.
 */
export function useDrupalSearch(
  options: DrupalSearchOptions = {}
): DrupalSearchController {
  const route = useRoute();
  const router = useRouter();

  const searchTerms = route.query.keys;
  const stateKey = `drupal-search-query:${route.path}`;
  const isSearchPanelOpen = useState<boolean>(
    'drupal-search-panel-open',
    () => false
  );

  const searchQuery = useState<string>(stateKey, () =>
    getSearchQueryValue(route.query.keys as string | string[] | undefined)
  );

  if (import.meta.client) {
    // TEMP: Stops user from navigating when search is open
    const isNavGuardRegistered = useState<boolean>(
      'drupal-search-nav-guard-registered',
      () => false
    );

    if (!isNavGuardRegistered.value) {
      isNavGuardRegistered.value = true;

      router.beforeEach((to, from) => {
        if (!isSearchPanelOpen.value) return true;
        if (to.fullPath === from.fullPath) return true;
        return false;
      });
    }

    // Locks the <body> from scrolling when Search pane is open
    const isBodyScrollLocked = useScrollLock(document.body);

    watch(
      isSearchPanelOpen,
      (isOpen) => {
        isBodyScrollLocked.value = isOpen;
      },
      { immediate: true }
    );
  }

  watch(
    () => route.query.keys,
    (keys) => {
      const nextValue = getSearchQueryValue(
        keys as string | string[] | undefined
      );

      if (searchQuery.value !== nextValue) {
        searchQuery.value = nextValue;
      }
    },
    { immediate: true }
  );

  /**
   * Submits the current keyword into the route query.
   *
   * Existing facet filters are preserved, pagination is implicitly reset by
   * omitting `page`, and the default sort is switched based on whether the
   * user is doing a keyword search or browsing without a keyword.
   */
  async function onSearch() {
    const value = searchQuery.value.trim();
    const query: Record<string, string | string[]> = {};

    searchQuery.value = value;

    for (const [key, currentValue] of Object.entries(route.query)) {
      if (key.startsWith('f')) {
        query[key] = currentValue as string | string[];
      }
    }

    if (options.preserveSort) {
      if (value) query.keys = value;
      if (route.query.sort_by) {
        query.sort_by = route.query.sort_by as string;
        if (route.query.sort_order) {
          query.sort_order = route.query.sort_order as string;
        }
      }
    } else if (value) {
      query.keys = value;
      query.sort_by = 'search_api_relevance';
      query.sort_order = 'DESC';
    } else {
      query.sort_by = 'created';
      query.sort_order = 'DESC';
    }

    const targetPath = resolveSearchTargetPath(route.path, options.targetPath);

    isSearchPanelOpen.value = false;
    await router.push({ path: targetPath, query });
  }

  function handleToggleSearchPanel() {
    if (import.meta.client) {
      const { isMegamenuOpen, handleToggleMegamenu } = useMenu();
      if (isMegamenuOpen.value) handleToggleMegamenu(null);
    }
    isSearchPanelOpen.value = !isSearchPanelOpen.value;
  }

  return {
    searchQuery,
    searchTerms,
    isSearchPanelOpen,
    onSearch,
    handleToggleSearchPanel,
  };
}
